import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { Col, Row } from 'react-bootstrap';
import Button from '@mui/material/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Langs, { Localize } from '../../app/lang/langs';
import { InfoTooltip } from './PermissionScopeTooltip';
import pStyles from './Permissions.module.css';


/** "Is manager" label + info bubble, shared by the modal and the profiles table header. */
export function IsManagerLabel() {
    return (
        <InfoTooltip
            heading={Localize('IS_MANAGER')}
            description={Localize('IS_MANAGER_DESC')}
            footer={Localize('IS_MANAGER_FOOTER')}
        >
            <span tabIndex={0} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'help' }}>
                <Langs str="IS_MANAGER" />
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'rgba(255, 144, 21, 0.8)' }} />
            </span>
        </InfoTooltip>
    )
}


export default function ProfileModal({ visible, onHide, isAddMode, editingProfile, onSave, teamOptions, profileOptions }) {
    let [formName, setFormName] = useState('')
    let [formDescription, setFormDescription] = useState('')
    let [formActive, setFormActive] = useState(true)
    let [formIsManager, setFormIsManager] = useState(false)
    let [formTeamIds, setFormTeamIds] = useState([])
    let [inheritFromProfileId, setInheritFromProfileId] = useState(null)

    useEffect(() => {
        if (visible) {
            if (isAddMode) {
                setFormName('')
                setFormDescription('')
                setFormActive(true)
                setFormIsManager(false)
                setFormTeamIds([])
                setInheritFromProfileId(null)
            } else if (editingProfile) {
                setFormName(editingProfile.name)
                setFormDescription(editingProfile.description || '')
                setFormActive(!!editingProfile.active)
                setFormIsManager(!!editingProfile.is_manager)
                setFormTeamIds(editingProfile.teams ? editingProfile.teams.map(t => t.id) : [])
            }
        }
    }, [visible, isAddMode, editingProfile])

    let handleSave = () => {
        if (!formName.trim()) return
        onSave({
            name: formName.trim(),
            description: formDescription.trim() || null,
            active: formActive,
            isManager: formIsManager,
            teamIds: formTeamIds,
            inheritFromProfileId: isAddMode ? inheritFromProfileId : null
        })
    }

    let modalHeader = () => (
        <div className={pStyles.profileModalHeader}>
            <Langs str={isAddMode ? 'ADD_PROFILE' : 'EDIT_PROFILE'} />
        </div>
    )

    let modalFooter = () => (
        <Row className="d-flex justify-content-center pt-3" style={{ borderTop: '1px solid rgba(0, 40, 128, 0.06)' }}>
            <Col lg={3} xs={4}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!formName.trim()}
                    className={pStyles.saveBtn}
                    fullWidth
                >
                    <Langs str={isAddMode ? 'ADD' : 'update'} />
                </Button>
            </Col>
        </Row>
    )

    return (
        <Dialog
            header={modalHeader}
            footer={modalFooter}
            visible={visible}
            className={`profile-mgmt-dialog ${pStyles.profileDialog}`}
            draggable={false}
            onHide={onHide}
        >
            <div className={pStyles.profileDialogContent}>
                <Row className={pStyles.profileFormRow}>
                    <Col lg={3} className={pStyles.profileFormLabel}>
                        <Langs str="Name" />
                    </Col>
                    <Col lg={9}>
                        <InputText
                            value={formName}
                            onChange={(e) => setFormName(e.target.value)}
                            placeholder={Localize('Name')}
                            className={`w-100 ${pStyles.profileInput}`}
                        />
                    </Col>
                </Row>

                <Row className={pStyles.profileFormRow}>
                    <Col lg={3} className={pStyles.profileFormLabel}>
                        <Langs str="description" />
                    </Col>
                    <Col lg={9}>
                        <InputText
                            value={formDescription}
                            onChange={(e) => setFormDescription(e.target.value)}
                            placeholder={Localize('description')}
                            className={`w-100 ${pStyles.profileInput}`}
                        />
                    </Col>
                </Row>

                {!isAddMode && (
                    <Row className={pStyles.profileFormRow}>
                        <Col lg={3} className={pStyles.profileFormLabel}>
                            <Langs str="active" />
                        </Col>
                        <Col lg={9} className="d-flex align-items-center" style={{ minHeight: 40 }}>
                            <InputSwitch
                                checked={formActive}
                                onChange={(e) => setFormActive(e.value)}
                            />
                        </Col>
                    </Row>
                )}

                <Row className={pStyles.profileFormRow}>
                    <Col lg={3} className={pStyles.profileFormLabel}>
                        <IsManagerLabel />
                    </Col>
                    <Col lg={9} className="d-flex align-items-center" style={{ minHeight: 40 }}>
                        <InputSwitch
                            checked={formIsManager}
                            onChange={(e) => setFormIsManager(e.value)}
                        />
                    </Col>
                </Row>

                <Row className={pStyles.profileFormRow}>
                    <Col lg={3} className={pStyles.profileFormLabel}>
                        <Langs str="TEAMS" />
                    </Col>
                    <Col lg={9}>
                        <MultiSelect
                            value={formTeamIds}
                            onChange={(e) => setFormTeamIds(e.value)}
                            options={teamOptions}
                            optionLabel="label"
                            optionValue="value"
                            placeholder={Localize('ALL_TEAMS')}
                            className="w-100"
                            filter
                            display="chip"
                            maxSelectedLabels={5}
                        />
                        <small className={pStyles.profileSmallText}>
                            <Langs str="EMPTY_MEANS_ALL_TEAMS" />
                        </small>
                    </Col>
                </Row>

                {isAddMode && profileOptions && profileOptions.length > 0 && (
                    <Row className={pStyles.profileFormRow}>
                        <Col lg={3} className={pStyles.profileFormLabel}>
                            <Langs str="INHERIT_PERMISSIONS" />
                        </Col>
                        <Col lg={9}>
                            <Dropdown
                                value={inheritFromProfileId}
                                onChange={(e) => setInheritFromProfileId(e.value)}
                                options={profileOptions}
                                optionLabel="name"
                                optionValue="id"
                                placeholder={Localize('NONE')}
                                className="w-100"
                                showClear
                                filter
                            />
                            <small className={pStyles.profileSmallText}>
                                <Langs str="INHERIT_PERMISSIONS_HELP" />
                            </small>
                        </Col>
                    </Row>
                )}
            </div>
        </Dialog>
    )
}
